import { log } from '@/lib/log';

/**
 * Validates an input element by checking the given html
 * attributes (e.g. minLength, maxLength, pattern, required, type)
 *
 * @param {HTMLInputElement} elem - Input element to validate
 * @returns {boolean} Validation result
 */
function validateInput(elem: HTMLInputElement): boolean {
	const trimmedValue = elem.value.trim();

	if (elem.hasAttribute('pattern')) {
		const patternRegex = new RegExp(elem.getAttribute('pattern') ?? '');

		log(`Testing ${trimmedValue} for pattern:`, patternRegex);

		return setValidity(elem, patternRegex.test(trimmedValue));
	} else if (elem.hasAttribute('minlength') || elem.hasAttribute('maxlength')) {
		const length = trimmedValue.length;
		const hasMinLength = elem.hasAttribute('minlength');
		const hasMaxLength = elem.hasAttribute('maxlength');
		const minLength = parseInt(elem.getAttribute('minlength') ?? '0', 10) || 0;
		const maxLength = parseInt(elem.getAttribute('maxlength') ?? '', 10) || Infinity;

		if (hasMinLength && !hasMaxLength) {
			return setValidity(elem, length >= minLength);
		} else if (!hasMinLength && hasMaxLength) {
			return setValidity(elem, length <= maxLength);
		} else if (hasMinLength && hasMaxLength) {
			return setValidity(elem, length >= minLength && length <= maxLength);
		}

		log(`Testing for minlength ${minLength} / maxlength ${maxLength}:`, length);
	} else if (elem.hasAttribute('min') || elem.hasAttribute('max')) {
		const value = parseInt(trimmedValue, 10);
		const isRequired = elem.hasAttribute('required');
		const hasMin = elem.hasAttribute('min');
		const hasMax = elem.hasAttribute('max');
		const minValue = parseInt(elem.getAttribute('min') ?? '0', 10) || 0;
		const maxValue = parseInt(elem.getAttribute('max') ?? '', 10) || Infinity;

		log(`Testing for min ${minValue} / max ${maxValue}:`, value);

		if (!isRequired && isNaN(value)) {
			return setValidity(elem, true);
		} else if (hasMin && !hasMax) {
			return setValidity(elem, value >= minValue);
		} else if (!hasMin && hasMax) {
			return setValidity(elem, value <= maxValue);
		} else if (hasMin && hasMax) {
			return setValidity(elem, value >= minValue && value <= maxValue);
		}
	}

	log('Testing for length > 0:', trimmedValue.length);

	return setValidity(elem, trimmedValue.length > 0);
}

/**
 * Sets the validity of an input element (shows / hides the error message form the data-validationError attribute)
 *
 * @param {HTMLInputElement} elem - Input element
 * @param {boolean} isValid - Whether the input is valid
 * @returns {boolean} Validation result
 */
function setValidity(elem: HTMLInputElement, isValid: boolean): boolean {
	const elemSelector = elem.name ? `[name=${elem.name}]` : `#${elem.id}`;

	if (isValid) {
		log('Valid input:', elemSelector);
		elem.setCustomValidity('');
		return true;
	}

	log(`Invalid input ${elemSelector}: ${elem.dataset.validationError}`);
	elem.setCustomValidity(elem.dataset.validationError ?? '');
	return false;
}

/**
 * Iterates over all inputs and validates them with the validateInput function
 *
 * @return {void}
 */
function validateInputs(): void {
	const inputs = document.querySelectorAll<HTMLInputElement>('input[required]');

	Array.from(inputs).map((input) => {
		return validateInput(input);
	});
}

export { validateInput, validateInputs };
